import { Helmet } from 'react-helmet-async';

const SITE_NAME     = 'BuildersLab';
const SITE_URL      = 'https://builderslab.net';
const DEFAULT_IMAGE = `${SITE_URL}/images/builderslab-logo.png`;
const DEFAULT_DESC     = 'BuildersLab uses AI to generate professional websites instantly. Describe your idea and our AI builds it in seconds — no coding required.';

/**
 * Only the title is worth passing on every page; everything else has a
 * sensible default, so the type check should not demand it.
 *
 * @param {object} props
 * @param {string} [props.title]
 * @param {string} [props.description]
 * @param {string} [props.ogImage]
 * @param {string} [props.ogUrl]    canonical URL; the site root when omitted
 * @param {string} [props.ogType]
 * @param {boolean} [props.noIndex]
 * @param {any} [props.jsonLd]      structured data, emitted as JSON-LD
 */
export default function SEOHead({
  title,
  description = DEFAULT_DESC,
  ogImage     = DEFAULT_IMAGE,
  ogUrl,
  ogType      = 'website',
  noIndex     = false,
  jsonLd,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonical = ogUrl || SITE_URL;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image"       content={ogImage} />
      <meta property="og:url"         content={canonical} />
      <meta property="og:type"        content={ogType} />
      <meta property="og:site_name"   content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={ogImage} />

      {/* Canonical */}
      <link rel="canonical" href={canonical} />

      {/* Structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
